import {
  put,
  select,
  apply,
  fork,
  delay,
  call,
} from 'redux-saga/effects';
import {
  getClientAlias,
  getLanguage,
  getQueryFeed,
  getMatchStatus,
  getBetstops,
  getOddsPanelConfig,
  getMatchMarkets,
  getMatchCurPeriod,
  getShowOdds,
} from 'reducers';
import { PreMatchOddsRequest, LiveOddsRequest, BookmakerOddsRequest } from 'fishnet-api';
import * as config from 'Config/fishnetUrls';
import { PREMATCH_ODDS_POLL_INTERVAL, LIVE_ODDS_POLL_INTERVAL } from 'constants/fishnet';
import { isNotStarted, isLive, isEnded } from 'utils/matchstatus';
import {
  sagaCancel,
  sagaRunning,
} from 'sagas/utilSagas';
import {
  updateLiveOdds, updatePreMatchOdds, updateLiveOddsFailed, updatePreMatchOddsFailed,
} from 'actions/fishnet/index';
import { pollFishnet, pollTillResponse, takeMatchUpdate } from '../utilLCR';


function* loadOdds(matchId, live = false, isBookmaker = false) {
  const showOdds = yield select(getShowOdds);
  if (!showOdds) {
    return;
  }
  const clientAlias = yield select(getClientAlias);
  const language = yield select(getLanguage);
  const oddsFeedsUrl = config.fishnetOddsFeedsUrl;
  const qFeed = yield select(getQueryFeed);
  const betstops = yield select(getBetstops);
  const hasOddsPanelConfig = yield select(getOddsPanelConfig);
  const periodNumber = yield select(getMatchCurPeriod, matchId);
  const matchMarkets = yield select(getMatchMarkets) || {};
  let request;
  if (isBookmaker) {
    request = new BookmakerOddsRequest(matchId, clientAlias, language, oddsFeedsUrl, qFeed);
  } else {
    request = live
      ? new LiveOddsRequest(matchId, clientAlias, language, oddsFeedsUrl, qFeed)
      : new PreMatchOddsRequest(matchId, clientAlias, language, oddsFeedsUrl, qFeed);
  }

  // returns null only for failed request, no odds returns object
  const normalizedOdds = yield apply(request, request.get, [matchMarkets, betstops, hasOddsPanelConfig, periodNumber]);


  // null to reducer is no-op
  if (normalizedOdds && !normalizedOdds.error) {
    if (live) {
      yield put(updateLiveOdds(normalizedOdds, matchId));
    } else {
      yield put(updatePreMatchOdds(normalizedOdds, matchId));
    }
  } else if (normalizedOdds && normalizedOdds.error) {
    if (live) {
      yield put(updateLiveOddsFailed(normalizedOdds, matchId));
    } else {
      yield put(updatePreMatchOddsFailed(normalizedOdds, matchId));
    }
    throw normalizedOdds;
  } else {
    throw new Error('Update Odds failed');
  }
}

// atleast once, different pre and live interval
function* pollOdds(matchId, pollLive = true) {
  let status;
  let taskPre;
  let taskLive;

  // always do atleast one request, to wipe out old data in state
  // does a BookmakerOddsRequest
  yield call(pollTillResponse, { logLevel: 5 }, loadOdds, matchId, pollLive, true); // atleast once

  while (true) {
    status = yield select(getMatchStatus, matchId);

    if (!sagaRunning(taskPre) && isNotStarted(status)) {
      // poll using prematch interval
      taskPre = yield fork(
        pollFishnet,
        { pollInterval: PREMATCH_ODDS_POLL_INTERVAL },
        loadOdds,
        matchId,
        false, // prematch
      );
    } else if (!sagaRunning(taskLive) && isLive(status) && pollLive) {
      taskPre = sagaCancel(taskPre); // cancel prematch polling
      // poll using prematch interval
      taskLive = yield fork(
        pollFishnet,
        { pollInterval: LIVE_ODDS_POLL_INTERVAL },
        loadOdds,
        matchId,
        true, // live
      );
    } else if (isEnded(status)) {
      yield delay(PREMATCH_ODDS_POLL_INTERVAL + 1000); // let it make one final request
      taskPre = sagaCancel(taskPre);
      taskLive = sagaCancel(taskLive);
    }

    yield call(takeMatchUpdate, matchId);
  }
}

export default pollOdds;
